import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import Container from "../../hoc/Container";
import OtherStorePurchasePanel from "../components/OtherStorePurchasePanel";
import OtherStoreRecentCodes from "../components/OtherStoreRecentCodes";
import OtherStoreGuide from "../components/OtherStoreGuide";
import { attachStoreImage } from "../helpers";
import { getOtherStoreGuide } from "../guides";
import { fetchOtherStore } from "../api";
import { getOtherStoreDefinition } from "../stores";
import { useOtherStoreReferralTracking } from "../useReferralTracking";

const CATALOG_REFRESH_MS = 30 * 1000;

function OtherStorePage() {
  useOtherStoreReferralTracking();
  const { storeId: routeStoreId } = useParams();
  const { pathname } = useLocation();
  const storeId = useMemo(() => {
    if (routeStoreId) {
      return routeStoreId;
    }
    const match = pathname.match(/^\/store\/([^/]+)/);
    return match?.[1] ?? null;
  }, [routeStoreId, pathname]);
  const [searchParams] = useSearchParams();
  const recentOrderId = searchParams.get("order");
  const recentOrderToken = searchParams.get("token");
  const definition = useMemo(
    () => getOtherStoreDefinition(storeId),
    [storeId],
  );
  const guide = getOtherStoreGuide(storeId);

  const [store, setStore] = useState(() =>
    definition ? attachStoreImage(definition) : null,
  );
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");

  const refreshCatalog = useCallback(
    async ({ silent = false } = {}) => {
      if (!definition) {
        return null;
      }

      if (!silent) {
        setCatalogLoading(true);
        setCatalogError("");
        setStore(attachStoreImage(definition));
      }

      try {
        const data = await fetchOtherStore(storeId);
        if (data?.regions?.length) {
          setStore(attachStoreImage(data));
          setCatalogError("");
          return data;
        }
        if (!silent) {
          setCatalogError(
            "Номиналы временно недоступны. Подождите минуту и обновите страницу.",
          );
        }
        return null;
      } catch (error) {
        if (!silent) {
          setCatalogError(
            error?.message || "Не удалось загрузить каталог. Попробуйте позже.",
          );
        }
        return null;
      } finally {
        if (!silent) {
          setCatalogLoading(false);
        }
      }
    },
    [definition, storeId],
  );

  useEffect(() => {
    let cancelled = false;

    refreshCatalog().finally(() => {
      if (!cancelled) {
        setCatalogLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [refreshCatalog]);

  useEffect(() => {
    if (!definition || catalogLoading || catalogError) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshCatalog({ silent: true });
    }, CATALOG_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [definition, catalogLoading, catalogError, refreshCatalog]);

  useEffect(() => {
    if (!store) {
      return;
    }
    window.ym?.(93997310, "hit", `/store/${store.id}`, {
      params: { title: store.pageTitle },
    });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [store]);

  if (!definition) {
    return <Navigate to="/" replace />;
  }

  if (!store) {
    return (
      <Container>
        <section className="content-block mb-2">
          <p className="other-store-description">Загрузка магазина…</p>
        </section>
      </Container>
    );
  }

  return (
    <Container>
      <section className="content-block mb-2">
        <nav className="other-store-breadcrumb">
          <Link to="/" className="other-store-breadcrumb-link">
            Главная
          </Link>
          <span aria-hidden="true">/</span>
          <span>{store.label}</span>
        </nav>

        <div className="other-store-layout">
          <article className="other-store-info-card">
            <div className="other-store-info-header">
              {store.image ? (
                <div
                  className={`other-store-thumb ${store.bgClass}`}
                  aria-hidden="true"
                >
                  <img src={store.image} alt="" aria-hidden="true" />
                </div>
              ) : (
                <div
                  className={`other-store-icon ${store.bgClass}`}
                  aria-hidden="true"
                >
                  <span>{store.label.charAt(0)}</span>
                </div>
              )}
              <h1 className="other-store-title">{store.pageTitle}</h1>
            </div>

            <p className="other-store-description">{store.description}</p>

            <dl className="other-store-meta">
              <div className="other-store-meta-row">
                <dt>Товар</dt>
                <dd>{store.genre}</dd>
              </div>
              <div className="other-store-meta-row">
                <dt>Разработчик</dt>
                <dd>{store.developer}</dd>
              </div>
            </dl>

            <OtherStoreRecentCodes
              storeId={storeId}
              orderId={recentOrderId}
              accessToken={recentOrderToken}
            />
          </article>

          <OtherStorePurchasePanel
            store={store}
            catalogLoading={catalogLoading}
            catalogError={catalogError}
            onRefreshCatalog={refreshCatalog}
          />
        </div>

        <div className="main-divider" />
        <OtherStoreGuide guide={guide} />
      </section>
    </Container>
  );
}

export default OtherStorePage;
